// src/components/dashboard/CompactDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import RetryDueCard from '../results/RetryDueCard';
import { loadGuestExamPending } from '../../utils/onboardingStorage';
import { adoptAnonExamSession } from '../../utils/sessionStore.js';
import { getExamSlug } from '../../utils/examSlug';
import { supportsPaper } from '../../utils/examMode';
import {
  Flame, Star, Calendar, BookOpen, Trophy, AlertCircle,
  Play, ArrowRight, ChevronRight, ChevronDown, Sparkles, BarChart2, TrendingUp, TrendingDown, Minus,
  GraduationCap, FileText, Clock, Crosshair, MessageSquare, Brain, Zap, RotateCw,
  Headphones, PenLine
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import SlideOutPanel from './SlideOutPanel';
import ActivityDetailPanel from './panels/ActivityDetailPanel';
import WeeklyDetailPanel from './panels/WeeklyDetailPanel';
import VocabularyDetailPanel from './panels/VocabularyDetailPanel';
import AchievementsDetailPanel from './panels/AchievementsDetailPanel';
import MistakesDetailPanel from './panels/MistakesDetailPanel';
import ExamAnalytics from '../exam/ExamAnalytics';
import examAnalyticsService from '../../services/examAnalyticsService';
import enhancedUserService from '../../services/enhancedUserService';
import examService from '../../services/examService';
// Import data transformers
import {
  loadGrammarData,
  loadVocabularyData,
  loadActivityData
} from '../../utils/dashboardDataTransformer';
// Import grammar topics map
import { GRAMMAR_TOPICS_BY_LEVEL } from '../../services/grammarTopicsMap';
// Import study plan progress service
import { getStudyPlanProgressForDashboard } from '../../services/studyPlanProgressService';
// Import animation components
import { PulseGlow, Float } from '../ui/AnimationComponents';
import ExamModePracticeHub from './ExamModePracticeHub';
import { getGoetheModuleSummary, getExamDisplayPercentage } from '../../utils/goetheExamDisplay';
import AbandonedPaymentBanner from './AbandonedPaymentBanner';
import { useAbandonedPayment } from '../../hooks/useAbandonedPayment';

const VOCAB_SHOWCASE_WORD = {
  word: 'die Überzeugung',
  translation: 'conviction, belief',
  category: 'Abstract Concepts',
};

const EXAM_SKILL_ICONS_BASE = [
  {
    Icon: BookOpen,
    labelKey: 'dashboard.examSkills.reading',
    color: '#93c5fd',
    bg: 'rgba(96,165,250,0.12)',
    border: 'rgba(96,165,250,0.3)',
    glow: 'rgba(96,165,250,0.35)',
  },
  {
    Icon: Headphones,
    labelKey: 'dashboard.examSkills.listening',
    color: '#c4b5fd',
    bg: 'rgba(167,139,250,0.12)',
    border: 'rgba(167,139,250,0.3)',
    glow: 'rgba(167,139,250,0.35)',
  },
  {
    Icon: PenLine,
    labelKey: 'dashboard.examSkills.writing',
    color: '#6ee7b7',
    bg: 'rgba(52,211,153,0.12)',
    border: 'rgba(52,211,153,0.3)',
    glow: 'rgba(52,211,153,0.35)',
  },
];


const CompactDashboard = ({ user, userProgress, isLoading, onExamSelectionComplete, personalizedPlan }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  // Unfinished guest exam from the /try signup gate: swaps the FTUE banner
  // for a "Continue your exam" CTA. Cleared by TryExamPage when the exam is
  // finished and by handleLogout; read once per mount (a finish always remounts
  // the dashboard). Scoped to this uid, so a marker left by another account on
  // a shared device stays invisible here.
  const [guestExamPending] = useState(() => loadGuestExamPending(user?.uid));
  
  // Create exam skills array with translated labels
  const EXAM_SKILL_ICONS = EXAM_SKILL_ICONS_BASE.map(skill => ({
    ...skill,
    label: t(skill.labelKey),
  }));
  
  const { abandonedPayment, dismiss: dismissPayment } = useAbandonedPayment(user?.uid);

  const [panelOpen, setPanelOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [activeSkillIndex, setActiveSkillIndex] = useState(0);
  const [isMockExamHovered, setIsMockExamHovered] = useState(false);

  useEffect(() => {
    if (isMockExamHovered) return;
    const interval = setInterval(() => {
      setActiveSkillIndex(i => (i + 1) % EXAM_SKILL_ICONS.length);
    }, 900);
    return () => clearInterval(interval);
  }, [isMockExamHovered, EXAM_SKILL_ICONS.length]);
  const [examAnalyticsOpen, setExamAnalyticsOpen] = useState(false);
    const [examAnalyticsData, setExamAnalyticsData] = useState(null);
  const [examHistory, setExamHistory] = useState([]);
  const [allExamGroups, setAllExamGroups] = useState({}); // key: "provider-level" (lowercase)
  const [bestGroupDefault, setBestGroupDefault] = useState(null); // fallback when target has no attempts
  const [isLoadingExamData, setIsLoadingExamData] = useState(false);
  const [nextLesson, setNextLesson] = useState(null);
  const [studyPlanData, setStudyPlanData] = useState(null);
  const [hasStudyPlan, setHasStudyPlan] = useState(false);
  // State for loaded data
  const [vocabularyData, setVocabularyData] = useState(null);
  const [mistakesData, setMistakesData] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentExamData, setCurrentExamData] = useState(null); // Store exam data for badge
  const [studyPlanProgress, setStudyPlanProgress] = useState({
    completed: 0,
    total: 32,
    estimatedTime: 12
  });
  const [onboardingPayload, setOnboardingPayload] = useState(null);
  const [onboardingReady, setOnboardingReady] = useState(false);
  const [showExamPracticeSection, setShowExamPracticeSection] = useState(false);
  const [showGeneralTrainingSection, setShowGeneralTrainingSection] = useState(false);
  const [firstRedemittelCard, setFirstRedemittelCard] = useState(null);
  const [previewCardFlipped, setPreviewCardFlipped] = useState(false);
  const [vocabPreviewFlipped, setVocabPreviewFlipped] = useState(false);
  const [grammarTopics, setGrammarTopics] = useState([]);
  const [grammarPreviewExercise, setGrammarPreviewExercise] = useState(null);
  const [grammarPreviewFlipped, setGrammarPreviewFlipped] = useState(false);

  // Load first Redemittel card for dashboard preview (by exam provider/level)
  useEffect(() => {
    let cancelled = false;
    const loadFirstRedemittel = async () => {
      const exam = currentExamData || onboardingPayload;
      const provider = (exam?.examProvider || exam?.examName?.split(/\s/)[0] || 'TELC').toLowerCase();
      const level = (exam?.examLevel || exam?.examName?.split(/\s/)[1] || 'B1').toLowerCase();
      try {
        const res = await fetch(`/data/redemittel/${provider}-${level}-redemittel.json`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || !data?.skills) return;
        const schreiben = data.skills.schreiben;
        const sprechen = data.skills.sprechen;
        const firstPart = schreiben?.parts?.[0] || sprechen?.parts?.[0];
        const firstCard = firstPart?.cards?.[0] || null;
        if (!cancelled && firstCard) setFirstRedemittelCard(firstCard);
      } catch (_) {
        if (!cancelled) setFirstRedemittelCard(null);
      }
    };
    loadFirstRedemittel();
    return () => { cancelled = true; };
  }, [currentExamData?.examProvider, currentExamData?.examLevel, onboardingPayload?.examName]);

  // Auto-flip preview card to suggest tap-to-flip in practice
  useEffect(() => {
    if (!firstRedemittelCard) return;
    const t = setInterval(() => setPreviewCardFlipped((f) => !f), 2500);
    return () => clearInterval(t);
  }, [firstRedemittelCard]);

  // Load additional data when component mounts
  useEffect(() => {
    const loadAdditionalData = async () => {
      if (!userProgress || !user) return;

      setIsLoadingData(true);

      try {
        // Get user's current level and language
        // Platform only supports B1, B2, C1 - convert A1/A2 to B1
        const userLevel = userProgress.preferences?.currentLevel || 'B1';
        const validLevels = ['B1', 'B2', 'C1'];
        const currentLevel = validLevels.includes(userLevel) ? userLevel : 'B1';
        const userLanguage = userProgress.preferences?.language || 'en';

        // Load vocabulary data
        const vocabData = await loadVocabularyData(user.uid, currentLevel, userLanguage);
        setVocabularyData(vocabData);

        // Load grammar/mistakes data
        const grammarData = await loadGrammarData(user.uid, currentLevel);
        setMistakesData(grammarData.mistakesData);
        // Extract weeklyData from grammarData (it's generated there)
        setWeeklyData(grammarData.weeklyData || []);

        // Load grammar topics for the preview badge
        const topicsForLevel = GRAMMAR_TOPICS_BY_LEVEL[currentLevel];
        if (topicsForLevel && topicsForLevel.topics) {
          // Get first 3 topics for preview
          setGrammarTopics(topicsForLevel.topics.slice(0, 3));
        }

        // Load activity data (for other activity-related features)
        const activityData = await loadActivityData(user.uid);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadAdditionalData();
  }, [user, userProgress]);

  // Load exam data from Firebase profile first (source of truth), then localStorage
  useEffect(() => {
    const loadExamDataForBadge = async () => {
      if (!user?.uid) {
        // Try localStorage fallback if no user
        try {
          const savedData = sessionStorage.getItem('selectedExamData') || localStorage.getItem('selectedExamData');
          if (savedData) {
            const parsed = JSON.parse(savedData);
            console.log('📦 Badge: Loaded exam from localStorage (no user):', parsed.examProvider, parsed.level);
            setCurrentExamData({
              examProvider: parsed.examProvider || 'TELC',
              examLevel: parsed.level || 'B2'
            });
            return;
          }
        } catch (error) {
          console.error('Error loading exam data from localStorage:', error);
        }
        return;
      }

      try {
        // First, check Firebase profile (source of truth)
        const profile = await enhancedUserService.getUserProfile(user.uid);
        
        if (profile) {
          // Check examSelection field (new structure)
          if (profile.examSelection && profile.examSelection.examProvider && profile.examSelection.level) {
            console.log('📦 Badge: Loaded exam from Firebase profile (examSelection):', profile.examSelection.examProvider, profile.examSelection.level);
            setCurrentExamData({
              examProvider: profile.examSelection.examProvider,
              examLevel: profile.examSelection.level
            });
            return;
          }
          
          // Check selectedExamProvider/selectedExamLevel (old structure)
          if (profile.selectedExamProvider && profile.selectedExamLevel) {
            console.log('📦 Badge: Loaded exam from Firebase profile (selectedExamProvider/selectedExamLevel):', profile.selectedExamProvider, profile.selectedExamLevel);
            setCurrentExamData({
              examProvider: profile.selectedExamProvider,
              examLevel: profile.selectedExamLevel
            });
            return;
          }
        }

        // Fallback to localStorage if Firebase doesn't have it
        const userId = user.uid;
        const userExamData = localStorage.getItem(`selectedExamData_${userId}`);
        if (userExamData) {
          const parsed = JSON.parse(userExamData);
          console.log('📦 Badge: Loaded exam from user-specific localStorage:', parsed.examProvider, parsed.level);
          setCurrentExamData({
            examProvider: parsed.examProvider || 'TELC',
            examLevel: parsed.level || 'B2'
          });
          return;
        }

        const savedData = sessionStorage.getItem('selectedExamData') || localStorage.getItem('selectedExamData');
        if (savedData) {
          const parsed = JSON.parse(savedData);
          console.log('📦 Badge: Loaded exam from general localStorage:', parsed.examProvider, parsed.level);
          setCurrentExamData({
            examProvider: parsed.examProvider || 'TELC',
            examLevel: parsed.level || 'B2'
          });
          return;
        }

        console.log('📦 Badge: No exam data found, using fallback TELC B2');
        setCurrentExamData({ examProvider: 'TELC', examLevel: 'B2' });
      } catch (error) {
        console.error('Error loading exam data for badge:', error);
        // Fallback on error
        setCurrentExamData({ examProvider: 'TELC', examLevel: 'B2' });
      }
    };

    loadExamDataForBadge();
  }, [user?.uid]);

  // Load onboarding payload for dashboard display (RequireOnboarding guard ensures user has completed onboarding)
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await enhancedUserService.getOnboardingComplete(user.uid);
        if (cancelled) return;
        if (data?.examName || data?.weakestSubject) setOnboardingPayload(data);
      } finally {
        if (!cancelled) setOnboardingReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const onboardingPurpose = onboardingPayload?.purpose || null; // kept for analytics; not used for layout
  const isExamOnly = true;
  const isTrainingOnly = false;

  // Visibility: wait for onboardingReady to avoid flash; collapsible toggles override hiding
  const examPracticeSectionVisible = onboardingReady && (!isTrainingOnly || showExamPracticeSection);
  const generalTrainingSectionVisible = onboardingReady && (!isExamOnly || showGeneralTrainingSection);

  // Helper function to get exam data (used in multiple places)
  // NOTE: This should use currentExamData state which is loaded from Firebase first
  const getUserExamData = () => {
    // Use state if available (loaded from Firebase first)
    if (currentExamData) {
      return currentExamData;
    }
    
    // Fallback to localStorage only if state not available yet
    try {
      const userId = user?.uid;
      if (userId) {
        const userExamData = localStorage.getItem(`selectedExamData_${userId}`);
        if (userExamData) {
          const parsed = JSON.parse(userExamData);
          console.log('⚠️ getUserExamData: Using localStorage fallback:', parsed.examProvider, parsed.level);
          return {
            examProvider: parsed.examProvider || 'TELC',
            examLevel: parsed.level || 'B2'
          };
        }
      }
      const savedData = sessionStorage.getItem('selectedExamData') || localStorage.getItem('selectedExamData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        console.log('⚠️ getUserExamData: Using localStorage fallback:', parsed.examProvider, parsed.level);
        return {
          examProvider: parsed.examProvider || 'TELC',
          examLevel: parsed.level || 'B2'
        };
      }
    } catch (error) {
      console.error('Error loading exam data:', error);
    }
    console.log('⚠️ getUserExamData: Using default fallback TELC B2');
    return { examProvider: 'TELC', examLevel: 'B2' };
  };

  // Load study plan and find next lesson
  useEffect(() => {
    const loadNextLesson = async () => {
      if (!userProgress && !user?.uid) return;

      try {
        const examData = getUserExamData();
        const examKey = `${examData.examProvider.toLowerCase()}-${examData.examLevel.toLowerCase()}`;
        
        // Fetch study plan JSON
        const response = await fetch(`/data/lessons/study-plans/${examKey}-study-plan.json`);
        if (!response.ok) {
          console.log('No study plan found - user needs to create one');
          setHasStudyPlan(false);
          setNextLesson(null);
          setStudyPlanData(null);
          return;
        }

        const studyPlan = await response.json();
        setStudyPlanData(studyPlan);
        setHasStudyPlan(true);

        // Get completed lessons
        const completedLessons = userProgress?.completedLessons || [];
        const completedSet = new Set(completedLessons);

        // Find next uncompleted lesson (skip video lessons that have no videoId)
        const allLessons = studyPlan.modules.flatMap(m => m.lessons || []);
        const nextUncompletedLesson = allLessons.find(lesson => {
          // Skip video lessons only if they have no linked video
          if (lesson.type === 'video' && !lesson.videoId) return false;
          return !completedSet.has(lesson.lessonId);
        });

        if (nextUncompletedLesson) {
          setNextLesson(nextUncompletedLesson);
        } else {
          // All lessons completed
          setNextLesson(null);
        }

        // Also update study plan progress for compatibility
        const progress = await getStudyPlanProgressForDashboard({
          profile: { completedLessons },
          recentSessions: userProgress?.recentSessions || [],
          completedLessons
        }, user?.uid);
        setStudyPlanProgress(progress);
      } catch (error) {
        console.error('Error loading next lesson:', error);
        setHasStudyPlan(false);
        setNextLesson(null);
        setStudyPlanData(null);
      }
    };

    loadNextLesson();
  }, [userProgress, user?.uid]);

// When cloud syncs writingFeedback to exam result, the doc does not get overallScore/sectionScores updated.
// Recalculate and persist so dashboard and exam history show the correct score without needing to open the results page.
const syncRecalculatedScoreIfNeeded = async (result, userId) => {
  const wf = result.writingFeedback;
  if (!wf || wf.status !== 'corrected') return;
  const hasScore = (wf.scores && (wf.scores.overallScore != null || wf.scores.scaledScore != null)) || wf.overallScore != null;
  if (!hasScore) return;
  const provider = (result.provider || result.examProvider || '').toUpperCase().replace('ÖSD', 'OSD');
  const level = (result.level || result.examLevel || 'Unknown').toString().toUpperCase();
  const aiWritingScore = wf.scores?.overallScore ?? wf.scores?.scaledScore ?? wf.overallScore;
  if (aiWritingScore == null) return;
  let writingScoreForCalc;
  if (provider === 'GOETHE') {
    writingScoreForCalc = Number(aiWritingScore);
  } else {
    const maxPts = wf.guidelineMetadata?.maxPoints ?? (level === 'C1' ? 48 : (provider === 'OSD' ? 30 : 45));
    writingScoreForCalc = (Number(aiWritingScore) / maxPts) * 100;
  }
  try {
    const examData = await examService.loadExam(provider, level);
    const updated = examService.calculateFinalScore(examData, result.userAnswers || {}, writingScoreForCalc, { writingFeedback: wf });
    const needsUpdate = provider === 'GOETHE'
      ? (updated.sectionScores && JSON.stringify(updated.sectionScores) !== JSON.stringify(result.sectionScores || {}))
      : (Math.round(updated.overallScore) !== Math.round(result.overallScore ?? 0));
    if (!needsUpdate) return;
    await enhancedUserService.updateExamResultWithFeedback(userId, result.id, {
      ...updated,
      writingFeedback: wf,
      writingData: result.writingData
    });
  } catch (e) {
    console.warn('Dashboard: sync recalculated score failed for result', result.id, e);
  }
};

// Load exam analytics with real-time listener (updates when writingFeedback is synced to exam result)
useEffect(() => {
  if (!user?.uid) return;

  setIsLoadingExamData(true);
  const resultsRef = collection(db, 'users', user.uid, 'examResults');
  const q = query(
    resultsRef,
    orderBy('completedAt', 'desc')
  );

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const allResults = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (allResults.length > 0) {
        const examGroups = {};
        const sortByCompleted = (a, b) => {
          const aTime = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : (a.completedAt || 0);
          const bTime = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : (b.completedAt || 0);
          return bTime - aTime;
        };

        allResults.forEach(result => {
          const provider = (result.provider || result.examProvider || 'Unknown').toString().trim();
          const level = (result.level || 'Unknown').toString().trim();
          const key = `${provider}-${level}`.toLowerCase();
          if (!examGroups[key]) {
            examGroups[key] = {
              provider,
              level,
              examId: result.examId,
              results: []
            };
          }
          examGroups[key].results.push(result);
        });

        Object.keys(examGroups).forEach(k => {
          examGroups[k].results.sort(sortByCompleted);
        });

        let bestGroup = null;
        let maxResults = 0;
        Object.values(examGroups).forEach(group => {
          if (group.results.length > maxResults) {
            maxResults = group.results.length;
            bestGroup = group;
          }
        });
        if (!bestGroup && allResults.length > 0) {
          const mostRecent = allResults[0];
          const p = (mostRecent.provider || mostRecent.examProvider || 'Unknown').toString().trim();
          const l = (mostRecent.level || 'Unknown').toString().trim();
          const key = `${p}-${l}`.toLowerCase();
          bestGroup = examGroups[key] || {
            provider: p,
            level: l,
            examId: mostRecent.examId,
            results: allResults.filter(r =>
              (r.provider || r.examProvider || '').toString().trim() === p && (r.level || '').toString().trim() === l
            )
          };
          if (bestGroup.results) bestGroup.results.sort(sortByCompleted);
        }

        setAllExamGroups(examGroups);
        setBestGroupDefault(bestGroup);

        // When cloud synced writingFeedback but not overallScore, recalc and persist so UI shows correct score
        const needSync = allResults.filter(r => r.writingFeedback?.status === 'corrected' && ((r.writingFeedback?.scores && (r.writingFeedback.scores.overallScore != null || r.writingFeedback.scores.scaledScore != null)) || r.writingFeedback?.overallScore != null));
        needSync.slice(0, 3).forEach(r => syncRecalculatedScoreIfNeeded(r, user.uid));
      } else {
        setAllExamGroups({});
        setBestGroupDefault(null);
      }
      setIsLoadingExamData(false);
    },
    (error) => {
      console.error('Error in exam results listener:', error);
      setIsLoadingExamData(false);
    }
  );

  return () => unsubscribe();
}, [user?.uid]);

  // Prefer user's target exam (currentExamData) for card and analytics; fall back to group with most attempts.
  // Live listener above keeps allExamGroups/bestGroupDefault in sync, so examHistory updates when results are synced (e.g. writingFeedback).
  useEffect(() => {
    const key = currentExamData
      ? `${(currentExamData.examProvider || '').toString().trim()}-${(currentExamData.examLevel || '').toString().trim()}`.toLowerCase()
      : null;
    let displayGroup = (key && allExamGroups[key]) ? allExamGroups[key] : bestGroupDefault;
    if (key && currentExamData && !allExamGroups[key])
      displayGroup = { provider: currentExamData.examProvider, level: currentExamData.examLevel, examId: null, results: [] };
    if (!displayGroup) {
      setExamHistory([]);
      return;
    }
    const sorted = (displayGroup.results || []).slice().sort((a, b) => {
      const at = a.completedAt?.toDate?.()?.getTime() ?? (a.completedAt || 0);
      const bt = b.completedAt?.toDate?.()?.getTime() ?? (b.completedAt || 0);
      return bt - at;
    });
    setExamHistory(sorted);
    const p = displayGroup.provider;
    const l = displayGroup.level;
    const normalizedProvider = (p && p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()) || 'Unknown';
    setExamAnalyticsData({
      examData: {
        examId: displayGroup.examId ?? null,
        examProvider: normalizedProvider,
        level: (l || '').toUpperCase()
      },
      currentExam: sorted[0] || null
    });
  }, [currentExamData, allExamGroups, bestGroupDefault]);

  // Extract REAL data from userProgress
  // Check preferences.dailyGoal first (where it's actually stored), then top-level dailyGoal, then default to 5
  const dailyGoal = userProgress?.preferences?.dailyGoal || userProgress?.dailyGoal || 5;
  const todayLessons = userProgress?.todayLessons || 0;
  const overallScore = userProgress?.grammarStats?.overallAccuracy || 0;

  // Vocabulary stats - use loaded data or fallback
  const vocabularyStats = userProgress?.vocabularyStats || vocabularyData?.stats || { mastered: 0, learning: 0, total: 0 };
  const finalVocabData = vocabularyData || userProgress?.vocabularyData || { categories: [], totalWords: 0, mistakeWords: [], sampleWord: null };
  const firstVocabPreviewWord = finalVocabData?.mistakeWords?.[0] || finalVocabData?.sampleWord || VOCAB_SHOWCASE_WORD;
  const _vocabArticleMatch = (firstVocabPreviewWord?.word || '').match(/^(der|die|das)\s+/i);
  const vocabArticle = _vocabArticleMatch ? _vocabArticleMatch[1].toLowerCase() : null;
  const vocabCleanWord = vocabArticle ? (firstVocabPreviewWord?.word || '').slice(vocabArticle.length + 1) : (firstVocabPreviewWord?.word || '');
  const _vocabArticleColorMap = {
    der: { bar: 'bg-blue-500', gradient: 'from-blue-400 to-blue-600', text: 'text-blue-400' },
    die: { bar: 'bg-pink-500', gradient: 'from-pink-400 to-pink-600', text: 'text-pink-400' },
    das: { bar: 'bg-green-500', gradient: 'from-green-400 to-green-600', text: 'text-green-400' },
  };
  const vocabColors = _vocabArticleColorMap[vocabArticle] || { bar: 'bg-gray-400', gradient: 'from-gray-400 to-gray-600', text: 'text-gray-500' };

  // Auto-flip vocabulary preview card in training mode
  useEffect(() => {
    if (!firstVocabPreviewWord) return;
    const t = setInterval(() => setVocabPreviewFlipped((f) => !f), 2500);
    return () => clearInterval(t);
  }, [firstVocabPreviewWord]);

  // Load first grammar exercise for dashboard preview
  useEffect(() => {
    let cancelled = false;
    const loadGrammarPreview = async () => {
      const userLevel = userProgress?.preferences?.currentLevel || 'B1';
      const validLevels = ['A2', 'B1', 'B2', 'C1'];
      const currentLevel = validLevels.includes(userLevel) ? userLevel : 'B1';
      try {
        const res = await fetch(`/data/grammar/grammar-${currentLevel.toLowerCase()}.json`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const exercises = data?.exercises || [];
        // Prefer sentenceBuilding for a more visual preview, else multipleChoice, else first
        const firstExercise =
          exercises.find(e => e.exerciseType === 'sentenceBuilding') ||
          exercises.find(e => e.exerciseType === 'multipleChoice') ||
          exercises[0] || null;
        if (!cancelled && firstExercise) {
          setGrammarPreviewExercise(firstExercise);
        }
      } catch (_) {
        if (!cancelled) setGrammarPreviewExercise(null);
      }
    };
    if (userProgress) loadGrammarPreview();
    return () => { cancelled = true; };
  }, [userProgress?.preferences?.currentLevel, userProgress?.preferences?.language]);

  // Auto-flip grammar preview card
  useEffect(() => {
    if (!grammarPreviewExercise) return;
    const timer = setInterval(() => setGrammarPreviewFlipped((f) => !f), 3000);
    return () => clearInterval(timer);
  }, [grammarPreviewExercise]);

  // Recent Activity Detection - REAL data (keeping for compatibility but won't display)
  const recentActivity = detectRecentActivity(userProgress, t);

  // Weekly data - use loaded or existing
  const finalWeeklyData = weeklyData.length > 0 ? weeklyData : (userProgress?.weeklyData || []);
  const activeDays = finalWeeklyData.filter(d => d.lessons > 0).length;

  // Achievements - REAL data
  const achievementStats = userProgress?.achievementStats || {};
  const unlockedAchievements = achievementStats.achievementsUnlocked || 0;
  
  // Study plan progress is now loaded from actual study plan JSON (see useEffect above)
  const handleNavigateToStudyPlan = () => {
    navigate('/study-plan');
  };

  // Handle starting a lesson from the study plan
  const handleStartLesson = (lesson) => {
    if (!lesson) return;

    // Handle video lessons, navigate to study plan with lesson in view
    if (lesson.type === 'video' && lesson.videoId) {
      navigate('/lessons', { state: { openLessonId: lesson.lessonId } });
      return;
    }

    // Handle grammar lessons
    if (lesson.lessonId.startsWith('grammar') || lesson.dataUrl?.includes('grammar')) {
      if (lesson.title?.toLowerCase().includes('practice') ||
          lesson.title?.toLowerCase().includes('übung') ||
          lesson.title?.toLowerCase().includes('training')) {
        navigate('/practice/grammar');
      } else {
        navigate('/study/grammar');
      }
      return;
    }

    // Handle strategy/theory lessons
    const strategyPageTypes = ['theory_practice', 'speaking_practice', 'writing_practice', 'strategy'];
    if (lesson.dataUrl || strategyPageTypes.includes(lesson.type)) {
      navigate('/lesson-strategy', {
        state: {
          dataUrl: lesson.dataUrl,
          currentLessonId: lesson.lessonId,
          studyPlan: studyPlanData,
          lessonStatusMap: {} // We don't have status map in dashboard, but it's optional
        }
      });
      return;
    }

    // Handle exam structure
    if (lesson.lessonId === 'exam_structure') {
      const examData = getUserExamData();
      navigate(`/exam-structure/${examData.examProvider.toLowerCase()}/${examData.examLevel.toLowerCase()}`);
      return;
    }

    // Default to study plan page
    navigate('/lessons');
  };
  // Mistakes - use loaded or existing
  const finalMistakesData = mistakesData || userProgress?.mistakesData || { totalMistakes: 0, topicBreakdown: [] };
  const topMistakes = finalMistakesData.topicBreakdown?.slice(0, 2) || [];

  const openPanel = (type) => {
    setActivePanel(type);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setTimeout(() => setActivePanel(null), 400);
  };

  const handleNavigation = (path) => {
    navigate(path);
    closePanel();
  };

  if (isLoading) {
    return <LoadingSkeleton t={t} />;
  }

  // Calculate zero state at component level for use in multiple places
  const totalXP = userProgress?.stats?.totalPoints || userProgress?.achievements?.totalPoints || 0;
  const achievementsUnlocked = userProgress?.achievementStats?.achievementsUnlocked || userProgress?.achievements?.unlocked?.length || 0;
  const hasCompletedAnyExam = examHistory.length > 0;
  // Hide banner and mock exam card once user has completed at least one exam (or has XP/achievements)
  const shouldShowZeroStateBanner = !hasCompletedAnyExam && totalXP === 0 && achievementsUnlocked === 0;

  const renderNextLessonCard = (orderClass = '') => {
    const hasPlan = hasStudyPlan && studyPlanData;
    const hasNextLesson = nextLesson !== null;
    const isVideoLesson = hasNextLesson && nextLesson.videoId;

    return (
      <div
        onClick={() => {
          if (hasNextLesson) {
            handleStartLesson(nextLesson);
          } else if (!hasPlan) {
            navigate('/study-plan/wizard');
          } else {
            navigate('/lessons');
          }
        }}
        className={`group relative bg-white text-slate-900 rounded-2xl p-4 sm:p-6 cursor-pointer border border-gray-200 shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 active:opacity-90 h-full ${orderClass} ${
          !hasPlan ? 'animate-pulse-glow' : ''
        }`}
        style={{
          touchAction: 'manipulation'
        }}
      >
        <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-gray-300 group-hover:border-slate-900 transition-colors" />
        <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-gray-300 group-hover:border-slate-900 transition-colors" />
        <div className="absolute top-3 right-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 z-10">
          <ArrowRight className="w-5 h-5 text-gray-600 sm:group-hover:text-slate-900 translate-x-1 sm:translate-x-0 sm:group-hover:translate-x-1 transition-all duration-300" />
        </div>
        <div className="relative z-10 h-full flex flex-col">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-space text-2xl font-black tracking-tight break-words text-slate-900">{t('dashboard.nextLesson.title', { defaultValue: 'Next Lesson' })}</h3>
                    {isVideoLesson && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                        <Play className="w-3 h-3" fill="currentColor" />
                        Video
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 font-medium break-words">
                    {hasPlan ? (hasNextLesson ? (isVideoLesson ? t('dashboard.nextLesson.watchVideo', { defaultValue: 'Watch video lesson' }) : t('dashboard.nextLesson.continue', { defaultValue: 'Continue learning' })) : t('dashboard.nextLesson.allCompleted', { defaultValue: 'All completed' })) : t('dashboard.nextLesson.createPlan', { defaultValue: 'Get started' })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Teacher-at-whiteboard badge, prominent for video lessons */}
                  {hasNextLesson && (
                    <div className={`rounded-2xl flex items-center justify-center group-hover:scale-105 transition-all duration-300 ${isVideoLesson ? 'w-14 h-14 bg-red-50 text-red-600 ring-2 ring-red-200' : 'w-12 h-12 bg-gray-200 text-slate-900'}`}>
                      <svg viewBox="0 0 40 40" className={isVideoLesson ? 'w-8 h-8' : 'w-7 h-7'} fill="none" xmlns="http://www.w3.org/2000/svg">
                        <style>{`
                          @keyframes teacherArmDash {
                            0%, 100% { transform: rotate(-18deg); }
                            40%       { transform: rotate(18deg); }
                            70%       { transform: rotate(-5deg); }
                          }
                          @keyframes boardWriteDash {
                            0%   { stroke-dashoffset: 30; opacity: 0; }
                            20%  { opacity: 1; }
                            60%  { stroke-dashoffset: 0; opacity: 1; }
                            85%, 100% { stroke-dashoffset: 0; opacity: 0.25; }
                          }
                          @keyframes boardLine2Dash {
                            0%, 30%   { stroke-dashoffset: 20; opacity: 0; }
                            55%       { opacity: 1; }
                            80%       { stroke-dashoffset: 0; opacity: 1; }
                            95%, 100% { stroke-dashoffset: 0; opacity: 0.25; }
                          }
                          .t-arm  { animation: teacherArmDash 2.4s ease-in-out infinite; transform-origin: 22px 21px; transform-box: fill-box; }
                          .b-txt1 { stroke-dasharray: 30; animation: boardWriteDash 2.4s ease-in-out infinite; }
                          .b-txt2 { stroke-dasharray: 20; animation: boardLine2Dash 2.4s ease-in-out infinite; }
                        `}</style>
                        {/* Whiteboard */}
                        <rect x="1" y="3" width="23" height="15" rx="1.5" fill="currentColor" opacity="0.12" stroke="currentColor" strokeWidth="1.2"/>
                        {/* Board stand */}
                        <line x1="12.5" y1="18" x2="9.5" y2="24" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
                        <line x1="12.5" y1="18" x2="15.5" y2="24" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
                        {/* Writing lines on board */}
                        <path className="b-txt1" d="M4 8.5 Q9 7 15 8.5 Q19 9.5 21 8.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none"/>
                        <path className="b-txt2" d="M4 12.5 Q8.5 11 14 12.5 Q17 13 20 12.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none"/>
                        {/* Teacher head */}
                        <circle cx="32" cy="9" r="3.2" fill="currentColor" opacity="0.85"/>
                        {/* Teacher body */}
                        <path d="M28.5 13.5 Q32 12 35.5 13.5 L35.5 22 Q32 23.5 28.5 22 Z" fill="currentColor" opacity="0.7"/>
                        {/* Animated arm */}
                        <g className="t-arm">
                          <line x1="29" y1="16.5" x2="22" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.85"/>
                          <circle cx="22" cy="13" r="1" fill="currentColor" opacity="0.85"/>
                        </g>
                        {/* Teacher legs */}
                        <line x1="30.5" y1="22" x2="29.5" y2="29" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/>
                        <line x1="33.5" y1="22" x2="34.5" y2="29" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.6"/>
                      </svg>
                    </div>
                  )}
                  {/* Book icon, hidden when it's a video lesson */}
                  {!isVideoLesson && (
                  <div className={`w-12 h-12 bg-gray-200 text-slate-900 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-gray-300 transition-all duration-300 ${!hasPlan ? 'animate-pulse' : ''}`}>
                  {hasNextLesson ? (
                    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <style>{`
                        @keyframes pageTurn {
                          0%   { transform: rotateY(0deg) skewY(0deg); opacity: 1; }
                          40%  { transform: rotateY(-70deg) skewY(-4deg); opacity: 0.7; }
                          60%  { transform: rotateY(-70deg) skewY(-4deg); opacity: 0.7; }
                          100% { transform: rotateY(0deg) skewY(0deg); opacity: 1; }
                        }
                        @keyframes pageShimmer {
                          0%, 100% { opacity: 0; }
                          45%, 55% { opacity: 0.35; }
                        }
                        .book-page { animation: pageTurn 2.2s ease-in-out infinite; transform-origin: left center; transform-box: fill-box; }
                        .page-shine { animation: pageShimmer 2.2s ease-in-out infinite; }
                      `}</style>
                      {/* Book base - left half */}
                      <rect x="2" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/>
                      {/* Book base - right half */}
                      <rect x="12" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/>
                      {/* Spine line */}
                      <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="1.2" opacity="0.5"/>
                      {/* Left page lines */}
                      <line x1="4" y1="9" x2="10" y2="9" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                      <line x1="4" y1="11.5" x2="10" y2="11.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                      <line x1="4" y1="14" x2="10" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                      {/* Right page lines (static, behind turning page) */}
                      <line x1="14" y1="9" x2="20" y2="9" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                      <line x1="14" y1="11.5" x2="20" y2="11.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                      <line x1="14" y1="14" x2="20" y2="14" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                      {/* Animated turning page */}
                      <g className="book-page">
                        <rect x="12" y="5" width="10" height="14" rx="1" fill="currentColor" opacity="0.55"/>
                        <line x1="14" y1="9" x2="20" y2="9" stroke="white" strokeWidth="0.9" opacity="0.6"/>
                        <line x1="14" y1="11.5" x2="20" y2="11.5" stroke="white" strokeWidth="0.9" opacity="0.6"/>
                        <line x1="14" y1="14" x2="20" y2="14" stroke="white" strokeWidth="0.9" opacity="0.6"/>
                        <rect className="page-shine" x="12" y="5" width="10" height="14" rx="1" fill="white"/>
                      </g>
                    </svg>
                  ) : hasPlan ? <Trophy className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
                  </div>
                  )}
                </div>
              </div>
              {hasNextLesson ? (
                <>
                  {isVideoLesson ? (
                    <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-900 flex-1 flex flex-col">
                      {/* Window chrome */}
                      <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 border-b border-gray-700">
                        <div className="w-2 h-2 rounded-full bg-red-400/80" />
                        <div className="w-2 h-2 rounded-full bg-yellow-400/80" />
                        <div className="w-2 h-2 rounded-full bg-green-400/80" />
                        <span className="text-[9px] font-mono text-gray-500 ml-2">Video-Lektion</span>
                      </div>
                      {/* Video body with play button and title */}
                      <div className="flex-1 flex flex-col items-center justify-center gap-3 px-5 py-5 bg-gradient-to-b from-gray-800 to-gray-900">
                        <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/30 group-hover:scale-110 transition-transform">
                          <Play className="w-6 h-6 text-white ml-0.5" fill="currentColor" />
                        </div>
                        <h3 className="font-space text-base sm:text-lg font-bold text-white/90 text-center leading-tight break-words">{nextLesson.title}</h3>
                      </div>
                      {/* Fake progress bar */}
                      <div className="h-1 bg-gray-800">
                        <div className="h-full w-0 bg-red-600 rounded-r-full" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-space text-3xl font-black mb-2 tracking-tight break-words text-slate-900">{nextLesson.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-gray-600">
                        {nextLesson.duration && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /><span>{nextLesson.duration} {t('dashboard.nextLesson.minutes', { defaultValue: 'min' })}</span></div>}
                        {nextLesson.xp && <div className="flex items-center gap-1"><Star className="w-3 h-3" /><span>{nextLesson.xp} XP</span></div>}
                      </div>
                      {hasPlan && studyPlanProgress && (
                        <div className="mt-4">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600 font-medium">{t('dashboard.nextLesson.progress', { defaultValue: 'Study Plan Progress' })}</span>
                            <span className="font-space font-bold text-xs text-slate-900">{studyPlanProgress.completed}/{studyPlanProgress.total}</span>
                          </div>
                          <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
                            <div className="bg-slate-900 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.round((studyPlanProgress.completed / studyPlanProgress.total) * 100)}%` }} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : hasPlan ? (
                <p className="text-sm text-gray-600">{t('dashboard.nextLesson.review', { defaultValue: 'Review your progress or take a practice exam.' })}</p>
              ) : (
                <p className="text-sm text-gray-600">{t('dashboard.nextLesson.createSubtitle', { defaultValue: 'Get a customized study plan tailored to your exam date and goals.' })}</p>
              )}
            </div>
          </div>
        </div>
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>
        <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-slate-900 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 transform scale-100 sm:scale-0 sm:group-hover:scale-100" />
      </div>
    );
  };

  return (
    <div className="relative min-h-full">
      {/* Static SVG grain texture, zero JS, zero paint cost */}
      <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.035]" aria-hidden xmlns="http://www.w3.org/2000/svg">
        <filter id="db-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#db-grain)" />
      </svg>
      {/* Main Dashboard - Squeezed when panel open */}
      <div
  className={`relative z-10 transition-all duration-500 ease-out ${
    panelOpen ? 'hidden md:block md:w-[58%] md:opacity-60 md:pointer-events-none' : 'w-full'
  }`}
>
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-4 md:space-y-6">

          {/* Abandoned checkout recovery banner */}
          {abandonedPayment && (
            <AbandonedPaymentBanner
              payment={abandonedPayment}
              onDismiss={dismissPayment}
            />
          )}

          {user?.uid && <RetryDueCard uid={user.uid} />}

          {/* FTUE Welcome Banner. Linear Noir (Low-Friction Exam CTA).
              With an unfinished guest exam (/try signup gate) the same banner
              becomes a "Continue your exam" CTA instead. */}
          {(() => {
            // Use component-level isZeroState
            const isZeroState = shouldShowZeroStateBanner;
            // Only offer to resume a pending exam that IS the user's exam. The
            // marker is device-wide and survives an account switch, so without
            // this a TELC C1 user could be shown a TELC B1 banner left behind by
            // whoever used the device before them. currentExamData is null until
            // the profile resolves, which keeps the wrong banner from flashing.
            const continueExam =
              guestExamPending &&
              currentExamData &&
              guestExamPending.provider === currentExamData.examProvider &&
              guestExamPending.level === currentExamData.examLevel
                ? guestExamPending
                : null;
            const continueExamName = continueExam
              ? `${continueExam.provider === 'OSD' ? 'ÖSD' : continueExam.provider} ${continueExam.level}`
              : '';

            return (continueExam || isZeroState) ? (
              <div
                className="
                  dashboard-welcome-banner
                  ftue-banner-entrance
                  group
                  relative
                  w-full
                  overflow-hidden
                  rounded-2xl
                  mb-6
                  transition-all
                  duration-500
                  hover:-translate-y-0.5
                  hover:scale-[1.02]
                "
                style={{
                  padding: '2px',
                  backgroundColor: '#121212',
                }}
              >
                {/* Animation 1: Perimeter Scanner Beam, rotating conic gradient border */}
                <div className="ftue-scanner-beam rounded-2xl" aria-hidden />
                
                {/* Inner container with dark background, reveals scanner beam on border */}
                <div
                  className="relative w-full h-full rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: '#121212',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
                  }}
                >
                  {/* Noise overlay (film grain) */}
                  <div className="ftue-noise-overlay rounded-2xl" aria-hidden />
                  
                  {/* Continuous ambient shimmer sweep */}
                  <div
                    className="absolute inset-0 animate-shimmer-sweep pointer-events-none rounded-2xl"
                    style={{
                      background: 'linear-gradient(to right, transparent, rgba(255, 255, 255, 0.05), transparent)',
                      width: '100%',
                    }}
                    aria-hidden
                  />

                  <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 p-4 sm:p-6 md:p-8">
                    {/* Technical icon with hover states + Animation 2: Radar Pulse */}
                    <div className="relative w-12 h-12 flex-shrink-0 ftue-text-reveal-1">
                      {/* Radar pulse behind icon */}
                      <div className="ftue-radar-pulse rounded-xl" aria-hidden />
                      {/* Icon container */}
                      <div className="relative w-full h-full bg-white/10 rounded-xl flex items-center justify-center border border-white/10 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                        <Crosshair className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  
                  {/* Copy section */}
                  <div className="flex-1 min-w-0 space-y-3" style={{ fontFamily: "'Inter', var(--font-sans), sans-serif" }}>
                    <h3 className="text-xl md:text-2xl font-bold tracking-tight text-white ftue-text-reveal-2">
                      {continueExam
                        ? t('dashboard.continueExam.title', { defaultValue: 'Finish Your Mock Exam.' })
                        : t('dashboard.zeroState.gamificationBanner.title', { defaultValue: 'Take Your First Mock Exam.' })}
                    </h3>
                    <div className="space-y-2 ftue-text-reveal-3">
                      <p className="text-white/60 text-sm md:text-base leading-relaxed">
                        {continueExam
                          ? t('dashboard.continueExam.subtitle', { exam: continueExamName, defaultValue: 'Your {{exam}} exam is waiting with your answers saved. Pick up where you stopped and complete the remaining sections.' })
                          : t('dashboard.zeroState.gamificationBanner.subtitle', { defaultValue: 'Find out exactly where you stand today. Complete a mock exam to establish your baseline score and generate your personalized study plan.' })}
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA button with glow + Animation 3: Periodic Shine */}
                  <div className="flex-shrink-0 ftue-text-reveal-4">
                    <button
                      type="button"
                      onClick={async () => {
                        // Both states (unfinished guest exam and zero state) go to
                        // the in-app simulator for the user's own exam. The guest
                        // page /try is a public, logged-out entry point; sending a
                        // signed-in user back out to it was disorienting. For a
                        // resume, the saved answers are adopted below so the
                        // simulator restores them under this uid.
                        // An unfinished guest exam that was sat on paper goes back
                        // to paper, on the same paper (its answers are saved on
                        // this device and taken over by the account, see useSittingState).
                        if (continueExam?.mode === 'paper' && supportsPaper(continueExam.provider, continueExam.level)) {
                          try {
                            const guestPaper = await examService.loadGuestExam(continueExam.provider, continueExam.level);
                            navigate(`/exams/${getExamSlug(continueExam.provider, continueExam.level)}/paper`, { state: { variantExamData: guestPaper } });
                            return;
                          } catch (paperErr) {
                            console.error('Could not reopen the paper exam; opening the digital one:', paperErr);
                          }
                        }
                        // Load exam data from Firebase first (source of truth), then localStorage
                        try {
                          const userId = user?.uid;
                          let examData = null;
                          let examProvider = null;
                          let examLevel = null;
                          
                          // First, try Firebase profile (source of truth)
                          if (userId) {
                            try {
                              const profile = await enhancedUserService.getUserProfile(userId);
                              
                              if (profile) {
                                // Check examSelection field (new structure)
                                if (profile.examSelection && profile.examSelection.examProvider && profile.examSelection.level) {
                                  examProvider = profile.examSelection.examProvider;
                                  examLevel = profile.examSelection.level;
                                  console.log('🎯 Exam Simulator: Loading from Firebase profile (examSelection):', examProvider, examLevel);
                                }
                                // Check selectedExamProvider/selectedExamLevel (old structure)
                                else if (profile.selectedExamProvider && profile.selectedExamLevel) {
                                  examProvider = profile.selectedExamProvider;
                                  examLevel = profile.selectedExamLevel;
                                  console.log('🎯 Exam Simulator: Loading from Firebase profile (selectedExamProvider/selectedExamLevel):', examProvider, examLevel);
                                }
                              }
                            } catch (firebaseError) {
                              console.error('Error loading from Firebase profile:', firebaseError);
                            }
                            
                            // If we got provider/level from Firebase, load the exam
                            if (examProvider && examLevel) {
                              try {
                                examData = await examService.loadExam(examProvider, examLevel);
                                console.log('✅ Exam Simulator: Loaded exam from Firebase:', examData.examName);
                                
                                // Save to localStorage for next time
                                localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
                                sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
                                
                                // Trigger event to update App.jsx state
                                window.dispatchEvent(new CustomEvent('examSelected', { detail: examData }));
                              } catch (loadError) {
                                console.error('Error loading exam from Firebase data:', loadError);
                                examData = null; // Reset to try localStorage fallback
                              }
                            }
                          }
                          
                          // Fallback to localStorage if Firebase didn't work
                          if (!examData) {
                            if (userId) {
                              const userExamData = localStorage.getItem(`selectedExamData_${userId}`);
                              if (userExamData) {
                                examData = JSON.parse(userExamData);
                                console.log('🎯 Exam Simulator: Using localStorage (user-specific):', examData.examProvider, examData.level);
                              }
                            }
                            
                            if (!examData) {
                              const savedData = sessionStorage.getItem('selectedExamData') || localStorage.getItem('selectedExamData');
                              if (savedData) {
                                examData = JSON.parse(savedData);
                                console.log('🎯 Exam Simulator: Using localStorage (general):', examData.examProvider, examData.level);
                              }
                            }
                          }
                          
                          // Check if examData has the required structure (examFormat property)
                          const hasValidStructure = examData && examData.examFormat && examData.examProvider && examData.level;
                          
                          if (hasValidStructure) {
                            // Resuming: hand the guest snapshot to this uid before the
                            // simulator mounts and reads its key, so the answers saved
                            // at the signup gate come back instead of a blank exam.
                            if (continueExam && userId) {
                              adoptAnonExamSession(userId, examData);
                            }
                            // Trigger event to update App.jsx state
                            if (userId) {
                              window.dispatchEvent(new CustomEvent('examSelected', { detail: examData }));
                            }
                            // Navigate to simulator with exam data in state as fallback
                            // ExamSimulatorPage can use this if App.jsx state hasn't updated yet
                            navigate('/exam/simulator', { state: { examData } });
                          } else {
                            // No valid examData, navigate to exam selection page first
                            navigate('/exam');
                          }
                        } catch (error) {
                          console.error('Error handling exam navigation:', error);
                          // Fallback to exam selection
                          navigate('/exam');
                        }
                      }}
                      className="
                        group/btn
                        relative
                        overflow-hidden
                        rounded-xl
                        px-5 py-3
                        md:px-6 md:py-3.5
                        font-semibold
                        text-black
                        bg-white
                        transition-all
                        duration-500
                        hover:-translate-y-0.5
                        hover:scale-105
                        active:scale-95
                        cursor-pointer
                        flex
                        items-center
                        justify-center
                        gap-2
                      "
                      style={{
                        fontFamily: "'Inter', var(--font-sans), sans-serif",
                        boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                        touchAction: 'manipulation'
                      }}
                    >
                      {/* Animation 3: Periodic CTA Shine, automatic shine sweep */}
                      <div className="ftue-cta-shine rounded-xl" aria-hidden />
                      
                      {/* Animated glow behind button on hover */}
                      <span className="ftue-button-glow-hover absolute left-1/2 top-1/2 w-[140%] h-[160%] pointer-events-none rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(255,255,255,0.3), transparent 70%)' }} aria-hidden />
                      
                      <span className="relative z-10">
                        {continueExam
                          ? t('dashboard.continueExam.cta', { defaultValue: 'Continue Exam' })
                          : t('dashboard.zeroState.gamificationBanner.cta', { defaultValue: 'Start Mock Exam' })}
                      </span>
                      <ArrowRight className="w-5 h-5 relative z-10 transition-transform duration-500 group-hover/btn:translate-x-1" />
                    </button>
                  </div>
                </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* Purpose-ordered sections: exam/both → exam first; training → general first */}
          <div className="flex flex-col gap-4 sm:gap-6">

          {/* ── EXAM SECTION GROUP ── */}
          <div className={!isTrainingOnly ? 'order-1' : 'order-2'}>

          {/* Exam Practice Section (Mock Exam, Latest Score, Next Lesson, Redemittel) */}
          {/* Toggle pill, only shown for training-only mode */}
          {onboardingReady && isTrainingOnly && (
            <div className="flex justify-center">
              <button
                onClick={() => setShowExamPracticeSection(s => !s)}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700/50 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                style={{ touchAction: 'manipulation' }}
              >
                {showExamPracticeSection
                  ? t('dashboard.toggles.hideExamFeatures')
                  : t('dashboard.toggles.showExamFeatures')}
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${showExamPracticeSection ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}

          {/* Collapsible wrapper for exam practice section */}
          <div className={`grid transition-all duration-500 ease-in-out ${examPracticeSectionVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
          <div className="space-y-4 sm:space-y-6">

          {/* Row 1: Take Your Mock Exam (left) | Next Lesson (right) - But only show Next Lesson if banner is hidden */}
          <div className={`grid gap-4 sm:gap-6 w-full ${
            shouldShowZeroStateBanner ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
          }`} style={{ direction: 'ltr' }}>
            {/* Take Your Mock Exam - left (hidden when zero-state banner is showing) */}
            {!shouldShowZeroStateBanner && (
            <motion.div
              onClick={() => navigate('/exam')}
              className="group relative bg-black text-white rounded-2xl p-4 sm:p-6 cursor-pointer border border-white/10 active:opacity-90 h-full md:order-1"
              style={{ touchAction: 'manipulation' }}
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onHoverStart={() => setIsMockExamHovered(true)}
              onHoverEnd={() => setIsMockExamHovered(false)}
            >
              {/* Shimmer sweep, kept, Target circle removed */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
              {/* Corner brackets */}
              <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-white/10 group-hover:border-white transition-colors" />
              <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-white/10 group-hover:border-white transition-colors" />
              {/* Arrow (mobile always visible, desktop on hover) */}
              <div className="absolute top-3 right-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 z-10">
                <ArrowRight className="w-5 h-5 text-white/60 sm:group-hover:text-white translate-x-1 sm:translate-x-0 sm:group-hover:translate-x-1 transition-all duration-300" />
              </div>

              <div className="relative z-10 h-full flex flex-col justify-between">
                {/* Top row: title left + animated icon trio right */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-space text-2xl font-black mb-1 tracking-tight text-white">
                      {t('dashboard.mockExamCard.titleLine1', { defaultValue: 'Take Your' })}
                      <br />
                      {t('dashboard.mockExamCard.titleLine2', { defaultValue: 'Mock Exam' })}
                    </h3>
                  </div>

                  {/* Sequential spotlight exam skill chips */}
                  <div className="flex flex-col items-end gap-1.5 pointer-events-none">
                    {EXAM_SKILL_ICONS.map(({ Icon, label, color, bg, border, glow }, i) => {
                      const lit = isMockExamHovered || activeSkillIndex === i;
                      return (
                        <motion.div
                          key={label}
                          animate={{
                            y: lit ? -5 : 0,
                            scale: lit ? 1.07 : 1,
                            opacity: lit ? 1 : 0.35,
                            boxShadow: lit ? `0 0 16px 4px ${glow}` : '0 0 0 0 transparent',
                          }}
                          transition={{ type: 'spring', stiffness: 400, damping: 24 }}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl"
                          style={{
                            background: bg,
                            border: `1px solid ${lit ? border : 'rgba(255,255,255,0.08)'}`,
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                          }}
                        >
                          <Icon style={{ color: lit ? color : 'rgba(255,255,255,0.35)' }} className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="text-xs font-medium" style={{ color: lit ? color : 'rgba(255,255,255,0.35)' }}>{label}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-auto">
                  <p className="text-sm text-white/60 font-medium leading-relaxed mb-4">
                    {t('dashboard.mockExamCard.subtitle', { defaultValue: 'Ready for the real thing? Take a timed mock exam to test your readiness.' })}
                  </p>
                  <div className="flex justify-between pt-4 border-t border-white/10">
                    <span className="text-xs text-white/60 font-medium">{t('dashboard.mockExamCard.badge', { defaultValue: 'Full Assessment' })}</span>
                    <span className="text-xs font-bold text-white flex items-center gap-1 group-hover:text-emerald-400 transition-colors">{t('dashboard.mockExamCard.cta', { defaultValue: 'Start Exam' })} <ArrowRight className="w-3 h-3"/></span>
                  </div>
                </div>
              </div>
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 transform scale-100 sm:scale-0 sm:group-hover:scale-100" />
            </motion.div>
            )}

            {/* Next Lesson - right (only show here if banner is hidden) */}
            {!shouldShowZeroStateBanner && renderNextLessonCard('md:order-2')}
          </div>

          {/* Full-width row: Exam mode practice hub */}
          <ExamModePracticeHub />

          {/* Row 3: Latest Exam Score | Redemittel (or Next Lesson | Latest Exam Score | Redemittel if banner showing) */}
          <div className={`grid gap-4 sm:gap-6 w-full ${
            shouldShowZeroStateBanner ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'
          }`}>
            {/* Next Lesson - only show in Row 3 if banner is showing */}
            {shouldShowZeroStateBanner && renderNextLessonCard()}

            {/* Latest Exam Score - left */}
            {(() => {
              const latestExam = examHistory.length > 0 ? examHistory[0] : null; // First item is newest (sorted desc)
              const previousExam = examHistory.length > 1 ? examHistory[1] : null;
              
              // Percentage for trend/chart: Goethe = (passed/total)*100, others = score/max
              const latestPercentage = getExamDisplayPercentage(latestExam);
              const previousPercentage = getExamDisplayPercentage(previousExam);
              
              // Calculate trend
              let trend = 'stable';
              let scoreDiff = 0;
              if (latestExam && previousExam) {
                scoreDiff = Math.round(latestPercentage - previousPercentage);
                if (scoreDiff > 0) trend = 'improving';
                else if (scoreDiff < 0) trend = 'declining';
              }

              // Get last 5 scores for mini chart (oldest to newest, left to right)
              const recentScores = examHistory
                .slice(0, 5)
                .reverse()
                .map(e => getExamDisplayPercentage(e));
              
              // Fill with placeholders if less than 5 exams (at the beginning)
              const chartData = [...Array(Math.max(0, 5 - recentScores.length)).fill(0), ...recentScores];

              return (
                <div className="md:order-1 h-full">
                <button
                  onClick={() => {
                    if (examAnalyticsData?.examData) {
                      setExamAnalyticsOpen(true);
                    }
                  }}
                  disabled={!examAnalyticsData?.examData}
                  className="group relative bg-white text-slate-900 rounded-2xl p-4 sm:p-6 cursor-pointer border border-gray-200 shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 active:opacity-90 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:active:opacity-100 h-full w-full text-left"
                  style={{
                    touchAction: 'manipulation'
                  }}
                >
                  {/* Corner decorations */}
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-gray-300 group-hover:border-gray-600 transition-colors" />
                  <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-gray-300 group-hover:border-gray-600 transition-colors" />

                  {/* Click indicator - Arrow in top right */}
                  {examAnalyticsData?.examData && (
                    <div className="absolute top-3 right-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowRight className="w-5 h-5 text-gray-600 sm:group-hover:text-slate-900 translate-x-1 sm:translate-x-0 sm:group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="relative z-10 flex flex-row justify-between items-stretch w-full h-full gap-4">
                    {/* Left Column - Stats & CTA */}
                    <div className="flex flex-col items-start justify-between flex-1 h-full min-w-0">
                      <div className="w-full">
                        <div className="flex items-start justify-between mb-3 pr-6">
                          <BarChart2 className="w-10 h-10 text-slate-900 group-hover:scale-110 transition-transform duration-300 flex-shrink-0" />
                          {latestExam && (currentExamData || examAnalyticsData?.examData) && (
                            <div className="text-right flex-shrink-0 min-w-0 ml-2">
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider truncate whitespace-nowrap">
                                {currentExamData?.examProvider ?? examAnalyticsData?.examData?.examProvider} {String(currentExamData?.examLevel ?? examAnalyticsData?.examData?.level ?? '').toUpperCase()}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {latestExam ? (
                          <>
                            <p className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wider leading-relaxed truncate whitespace-nowrap">
                              {t('dashboard.examPerformance.latestScore', { defaultValue: 'Latest Exam Score' })}
                            </p>
                            <div className="flex items-end gap-2 mb-3">
                              <p className="font-space text-4xl sm:text-5xl font-black text-slate-900">
                                {getGoetheModuleSummary(latestExam)
                                  ? `${getGoetheModuleSummary(latestExam).displayShort} modules`
                                  : `${Math.round(latestPercentage)}%`}
                              </p>
                              {previousExam && (
                                <div className={`flex items-center gap-1 mb-2 px-2 py-1 rounded-lg text-xs font-bold ${
                                  trend === 'improving' ? 'bg-green-100 text-green-700' :
                                  trend === 'declining' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {trend === 'improving' && <TrendingUp className="w-3 h-3" />}
                                  {trend === 'declining' && <TrendingDown className="w-3 h-3" />}
                                  {trend === 'stable' && <Minus className="w-3 h-3" />}
                                  {Math.abs(scoreDiff)}%
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-4">
                            <p className="text-sm text-gray-600 mb-1 font-medium uppercase tracking-wider leading-relaxed">
                              {t('dashboard.examPerformance.title', { defaultValue: 'Exam Performance' })}
                            </p>
                            <p className="text-xs text-gray-500 text-center">
                              {t('dashboard.examPerformance.noExams', { defaultValue: 'Take a mock exam to see your performance' })}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Bottom CTA */}
                      {latestExam && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 w-full mt-auto">
                          <p className="text-xs text-gray-500 font-medium leading-tight pr-2">
                            {examHistory.length} {t('dashboard.examPerformance.examsCompleted', { defaultValue: 'exams completed' })}
                          </p>
                          {examAnalyticsData?.examData && (
                            <p className="text-xs text-gray-400 font-medium opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 truncate whitespace-nowrap flex-shrink-0">
                              {t('dashboard.examPerformance.viewAnalytics', { defaultValue: 'Take your next exam →' })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right Column - Taller Bar Chart */}
                    {latestExam && chartData.length > 0 && recentScores.length > 0 && (
                      <div className="flex flex-col justify-end items-end w-1/2 max-w-[240px] flex-shrink-0 h-full pt-6 sm:pt-0 z-10">
                        <div className="flex items-end justify-between gap-2 sm:gap-4 h-24 sm:h-28 w-full">
                          {chartData.map((score, i) => (
                            <div key={i} className="flex-1 flex flex-col justify-end h-full min-w-0">
                              <div
                                className={`w-full rounded-t transition-all duration-500 flex-shrink-0 ${
                                  i === chartData.length - 1 ? 'bg-slate-900' : 'bg-gray-300'
                                }`}
                                style={{ height: `${Math.max(score, 5)}%` }}
                              />
                            </div>
                          ))}
                        </div>
                        <p className="text-[11px] sm:text-[10px] text-gray-400 text-center font-medium uppercase tracking-widest mt-1 w-full">
                          {t('dashboard.examPerformance.lastAttempts', { defaultValue: 'Last 5 Attempts' })}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>

                  {/* Particle dots */}
                  <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-slate-900 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 transform scale-100 sm:scale-0 sm:group-hover:scale-100" />
                </button>
                </div>
              );
            })()}

            {/* Redemittel - Badge with narrow horizontal flip preview card */}
              <div
                onClick={() => navigate('/practice/redemittel')}
                className="group relative bg-black text-white rounded-2xl p-4 sm:p-5 cursor-pointer transition-all duration-500 hover:-translate-y-1 active:opacity-90 flex flex-col md:order-2"
                style={{
                  touchAction: 'manipulation'
                }}
              >
                <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-white/20 group-hover:border-white transition-colors" />
                <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-white/20 group-hover:border-white transition-colors" />
                <div className="relative z-10 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-space text-2xl font-black mb-1 tracking-tight text-white">{t('dashboard.redemittel.title', { defaultValue: 'Redemittel' })}</h3>
                      <p className="text-sm text-white/60 font-medium leading-relaxed">{t('dashboard.redemittel.subtitle', { defaultValue: 'Useful phrases for writing and speaking' })}</p>
                    </div>
                    <div className="w-12 h-12 bg-white/10 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 group-hover:bg-white/20 transition-all duration-300 flex-shrink-0">
                      <MessageSquare className="w-6 h-6" />
                    </div>
                  </div>
                  {firstRedemittelCard ? (
                    <div className="mb-4 flex flex-col">
                      <div className="w-full max-w-[280px] h-[88px] relative mx-auto" style={{ perspective: '420px' }}>
                        <div className="relative w-full h-full transition-transform duration-700 ease-in-out" style={{ transformStyle: 'preserve-3d', transform: previewCardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>
                          <div className="absolute inset-0 rounded-xl border border-white/10 bg-zinc-900 flex overflow-hidden" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}>
                            <div className="h-full w-1 bg-gradient-to-b from-purple-500 to-blue-600 flex-shrink-0" />
                            <div className="flex flex-1 items-center gap-3 p-3 min-w-0">
                              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-purple-400 to-blue-600 flex items-center justify-center flex-shrink-0 text-xl">{firstRedemittelCard.icon || '💬'}</div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{firstRedemittelCard.front}</p>
                                {firstRedemittelCard.context && <span className="text-[10px] text-white/50 truncate block mt-0.5">{firstRedemittelCard.context}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="absolute inset-0 rounded-xl border border-white/10 bg-white/5 flex overflow-hidden" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                            <div className="h-full w-1 bg-gradient-to-b from-purple-500 to-blue-600 flex-shrink-0" />
                            <div className="flex flex-1 items-center p-3 min-w-0">
                              <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{firstRedemittelCard.back}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-3 min-h-[72px] mb-2">
                      <p className="text-white/60 font-medium text-sm text-center leading-relaxed">{t('practice.options.redemittel.descriptionShort', { defaultValue: 'Useful phrases & expressions' })}</p>
                    </div>
                  )}
                  <span className="inline-flex items-center gap-2 text-white font-semibold text-sm group-hover:gap-3 transition-all">{t('dashboard.redemittel.cta', { defaultValue: 'Practice Redemittel' })}<ArrowRight className="w-4 h-4" /></span>
                </div>
                <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>
                <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-0 group-hover:scale-100" />
              </div>
          </div>
          </div>{/* end space-y-4 sm:space-y-6 */}
          </div>{/* end overflow-hidden */}
          </div>{/* end collapsible grid, exam practice section */}
          </div>{/* end exam section group */}

          {/* ── GENERAL TRAINING SECTION GROUP ── */}
          <div className={!isTrainingOnly ? 'order-2' : 'order-1'}>

          {/* Divider toggle, only shown for exam-only mode to reveal general training */}
          {onboardingReady && isExamOnly && (
            <div className="flex items-center gap-3 py-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
              <button
                onClick={() => setShowGeneralTrainingSection(s => !s)}
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700/50 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                style={{ touchAction: 'manipulation' }}
              >
                <Brain className="w-4 h-4 flex-shrink-0" />
                {showGeneralTrainingSection ? t('dashboard.toggles.hideGeneralTraining') : t('dashboard.toggles.generalTraining')}
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${showGeneralTrainingSection ? 'rotate-180' : ''}`} />
              </button>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
            </div>
          )}

          {/* Collapsible wrapper, general training section (vocab, grammar, weekly, achievements) */}
          <div className={`grid transition-all duration-500 ease-in-out ${generalTrainingSectionVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
          <div className="space-y-4 sm:space-y-6">

          {/* Three Badges Row: Vocabulary, Grammar Weak Points, Grammar Exercises */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Vocabulary by Category - White */}
            {(() => {
              if (!onboardingReady) return null;
              // Fix: Check learning/mastered stats instead of totalWords
              const isZeroState = (vocabularyStats.learning || 0) === 0 && (vocabularyStats.mastered || 0) === 0;
              // Use exam level from Firebase (currentExamData) instead of preferred level
              // Fallback to preferred level if exam data not available, then default to B1
              const examLevel = currentExamData?.examLevel;
              const preferredLevel = userProgress?.preferences?.currentLevel;
              const userLevel = examLevel || preferredLevel || 'B1';
              const validLevels = ['B1', 'B2', 'C1'];
              const currentLevel = validLevels.includes(userLevel) ? userLevel : 'B1';
              
              return (
                <div
                  onClick={() => {
                    if (isZeroState) {
                      navigate('/practice/vocabulary');
                    } else {
                      openPanel('vocabulary');
                    }
                  }}
                  className="group relative bg-black text-white rounded-2xl p-4 sm:p-5 cursor-pointer border border-white/10 transition-all duration-500 hover:-translate-y-1 active:opacity-90"
                  style={{
                    touchAction: 'manipulation'
                  }}
                >
                  {/* Corner decorations */}
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-white/10 group-hover:border-white transition-colors" />
                  <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-white/10 group-hover:border-white transition-colors" />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-space text-2xl font-black mb-1 tracking-tight text-white">{t('dashboard.vocabularyCategories.title')}</h3>
                        <p className="text-sm text-white/60 font-medium leading-relaxed">
                          {isZeroState
                            ? t('dashboard.zeroState.vocabulary.subtitle', { defaultValue: 'Ready to learn?' })
                            : `${finalVocabData.categories?.length || 0} ${t('dashboard.vocabBreakdown.categories')}`
                          }
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-white/10 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 group-hover:bg-white/20 transition-all duration-300">
                        <BookOpen className="w-6 h-6" />
                      </div>
                    </div>

                    {isLoadingData ? (
                      <div className="space-y-3 animate-pulse">
                        <div className="h-10 bg-white/10 rounded-lg"></div>
                        <div className="flex gap-2">
                          <div className="flex-1 h-14 bg-white/10 rounded-lg"></div>
                          <div className="flex-1 h-14 bg-white/10 rounded-lg"></div>
                        </div>
                      </div>
                    ) : isZeroState ? (
                      <div className="flex flex-col items-center justify-center py-5 px-3">
                        <BookOpen className="w-14 h-14 text-white/40 mb-3" />
                        <p className="text-white/80 font-medium text-base mb-4 text-center">
                          {t('dashboard.zeroState.vocabulary.emptyState', { defaultValue: 'Ready to build your vocabulary?' })}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/practice/vocabulary');
                          }}
                          className="bg-white text-black cursor-pointer px-4 py-2 rounded-md hover:bg-white/90 active:scale-95 transition-all font-semibold flex items-center gap-2"
                          style={{ touchAction: 'manipulation' }}
                        >
                          {t('dashboard.vocabulary.unlockDeck', { 
                            level: currentLevel || 'B1',
                            defaultValue: `Unlock ${currentLevel || 'B1'} Deck`
                          })}
                          <ArrowRight className="w-4 h-4" />
                        </button>
                        {firstVocabPreviewWord && (
                          <div className="mt-4 w-full max-w-[260px]">
                            <div className="h-[88px] relative mx-auto" style={{ perspective: '420px' }}>
                              <div
                                className="relative w-full h-full transition-transform duration-700 ease-in-out"
                                style={{
                                  transformStyle: 'preserve-3d',
                                  transform: vocabPreviewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                                }}
                              >
                                {/* Front: German word */}
                                <div
                                  className="absolute inset-0 rounded-xl border border-white/10 bg-zinc-900 flex overflow-hidden"
                                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                                >
                                  <div className={`h-full w-1 ${vocabColors.bar} flex-shrink-0`} />
                                  <div className="flex flex-1 items-center gap-3 p-3 min-w-0">
                                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${vocabColors.gradient} flex items-center justify-center text-sm flex-shrink-0`}>📚</div>
                                    <div className="min-w-0 flex-1">
                                      {vocabArticle && <p className={`text-[10px] font-semibold ${vocabColors.text} leading-none mb-0.5`}>{vocabArticle}</p>}
                                      <p className="font-semibold text-white text-sm leading-snug line-clamp-1">{vocabCleanWord}</p>
                                      {firstVocabPreviewWord?.category && <span className="text-[10px] text-white/40 truncate block mt-0.5">{firstVocabPreviewWord.category}</span>}
                                    </div>
                                  </div>
                                </div>
                                {/* Back: translation */}
                                <div
                                  className="absolute inset-0 rounded-xl border border-white/10 bg-white/5 flex overflow-hidden"
                                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                >
                                  <div className={`h-full w-1 ${vocabColors.bar} flex-shrink-0`} />
                                  <div className="flex flex-1 items-center p-3 min-w-0">
                                    <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{firstVocabPreviewWord?.translation}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-space text-3xl sm:text-4xl font-black text-white">{finalVocabData.totalWords || 0}</span>
                          <span className="text-sm text-white/60 font-medium leading-relaxed">{t('dashboard.vocabBreakdown.wordsLearned')}</span>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 bg-white/10 rounded-lg px-3 py-3 text-center border border-white/10">
                            <p className="text-xs text-white/60 mb-1 font-medium uppercase tracking-wider">{t('dashboard.vocabBreakdown.stats.mastered')}</p>
                            <p className="font-space font-black text-xl text-white">{vocabularyStats.mastered || 0}</p>
                          </div>
                          <div className="flex-1 bg-white/10 rounded-lg px-3 py-3 text-center border border-white/10">
                            <p className="text-xs text-white/60 mb-1 font-medium uppercase tracking-wider">{t('dashboard.vocabBreakdown.stats.learning')}</p>
                            <p className="font-space font-black text-xl text-white">{vocabularyStats.learning || 0}</p>
                          </div>
                        </div>
                        {firstVocabPreviewWord && (
                          <div className="mt-3">
                            <div className="w-full max-w-[260px] h-[88px] relative mx-auto" style={{ perspective: '420px' }}>
                              <div
                                className="relative w-full h-full transition-transform duration-700 ease-in-out"
                                style={{
                                  transformStyle: 'preserve-3d',
                                  transform: vocabPreviewFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                                }}
                              >
                                {/* Front: German word */}
                                <div
                                  className="absolute inset-0 rounded-xl border border-white/10 bg-zinc-900 flex overflow-hidden"
                                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                                >
                                  <div className={`h-full w-1 ${vocabColors.bar} flex-shrink-0`} />
                                  <div className="flex flex-1 items-center gap-3 p-3 min-w-0">
                                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${vocabColors.gradient} flex items-center justify-center text-sm flex-shrink-0`}>📚</div>
                                    <div className="min-w-0 flex-1">
                                      {vocabArticle && <p className={`text-[10px] font-semibold ${vocabColors.text} leading-none mb-0.5`}>{vocabArticle}</p>}
                                      <p className="font-semibold text-white text-sm leading-snug line-clamp-1">{vocabCleanWord}</p>
                                      {firstVocabPreviewWord?.category && <span className="text-[10px] text-white/40 truncate block mt-0.5">{firstVocabPreviewWord.category}</span>}
                                    </div>
                                  </div>
                                </div>
                                {/* Back: translation */}
                                <div
                                  className="absolute inset-0 rounded-xl border border-white/10 bg-white/5 flex overflow-hidden"
                                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                >
                                  <div className={`h-full w-1 ${vocabColors.bar} flex-shrink-0`} />
                                  <div className="flex flex-1 items-center p-3 min-w-0">
                                    <p className="font-semibold text-white text-sm leading-snug line-clamp-2">{firstVocabPreviewWord?.translation}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>

                  {/* Particle dots */}
                  <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-0 group-hover:scale-100" />
                </div>
              );
            })()}

            {/* Grammar Weak Points - White with Red accents */}
            {(() => {
              const isZeroState = (finalMistakesData.totalMistakes || 0) === 0 && (topMistakes.length || 0) === 0;
              
              return (
                <div
                  onClick={() => {
                    if (isZeroState) {
                      navigate('/practice/grammar');
                    } else {
                      openPanel('mistakes');
                    }
                  }}
                  className={`group relative bg-white text-slate-900 rounded-2xl p-4 sm:p-5 cursor-pointer border border-gray-200 shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 active:opacity-90 ${
                    isZeroState ? 'animate-pulse-glow' : ''
                  }`}
                  style={{
                    touchAction: 'manipulation'
                  }}
                >
                  {/* Decorative circle, inside overflow wrapper so it doesn't bleed outside card */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                    <div className={`absolute top-0 right-0 w-24 h-24 bg-red-500 opacity-5 rounded-full transform -translate-y-8 translate-x-8 ${
                      isZeroState ? 'animate-pulse' : ''
                    }`} />
                  </div>

                  {/* Corner decorations */}
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-gray-300" />
                  <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-gray-300" />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-space text-2xl font-black mb-1 tracking-tight text-slate-900">{t('dashboard.mistakes.title')}</h3>
                        <p className="text-sm text-gray-600 font-medium leading-relaxed">
                          {isZeroState 
                            ? t('dashboard.zeroState.mistakes.subtitle', { defaultValue: 'No mistakes yet' })
                            : `${topMistakes.length} ${t('dashboard.mistakes.topicsNeedReview')}`
                          }
                        </p>
                      </div>
                      <div className={`w-12 h-12 bg-red-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 border-2 border-red-200 text-white ${
                        isZeroState ? 'animate-pulse' : ''
                      }`}>
                        <AlertCircle className="w-6 h-6" />
                      </div>
                    </div>

                    {isLoadingData ? (
                      <div className="space-y-2 animate-pulse">
                        <div className="h-10 bg-gray-200 rounded-lg"></div>
                        <div className="h-10 bg-gray-200 rounded-lg"></div>
                      </div>
                    ) : isZeroState ? (
                      <div className="space-y-2">
                        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border-2 border-red-200">
                          <p className="text-center text-slate-900 font-space text-base font-bold mb-2 leading-relaxed">
                            {t('dashboard.zeroState.mistakes.cta', { 
                              defaultValue: 'Take a quick placement test to find your grammar weak spots.'
                            })}
                          </p>
                          <div className="flex items-center justify-center gap-2 mt-3">
                            <ArrowRight className="w-5 h-5 animate-pulse text-red-600" />
                            <span className="text-sm text-red-700 font-medium leading-relaxed">
                              {t('dashboard.zeroState.mistakes.action', { defaultValue: 'Start Grammar Practice →' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {topMistakes.length > 0 ? topMistakes.map((mistake, i) => (
                          <div key={i} className="bg-gray-100 rounded-lg px-4 py-2.5 flex items-center justify-between border border-gray-300 group-hover:bg-gray-200 transition-colors">
                            <span className="text-sm font-medium text-slate-900 leading-relaxed">{mistake.name}</span>
                            <span className="text-xs text-red-600 font-bold">
                              {mistake.mistakes} {t('dashboard.mistakes.topicCard.mistakes')}
                            </span>
                          </div>
                        )) : (
                          <p className="text-sm text-white/60 text-center py-3 leading-relaxed">{t('dashboard.mistakes.noMistakes.title')}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>

                  {/* Particle dot */}
                  <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 transform scale-100 sm:scale-0 sm:group-hover:scale-100" />
                </div>
              );
            })()}

            {/* Grammar Exercises - Black with Blue accents */}
            {(() => {
              const exerciseTypeLabel = (type) => {
                const labels = {
                  multipleChoice: 'Multiple Choice',
                  sentenceTransformation: 'Sentence Building',
                  gapFill: 'Fill in the Blank',
                  freeProduction: 'Free Writing',
                };
                return labels[type] || type;
              };
              return (
                <div
                  onClick={() => navigate('/practice/grammar')}
                  className="group relative bg-black text-white rounded-2xl p-4 sm:p-5 cursor-pointer border border-white/10 transition-all duration-500 hover:-translate-y-1 active:opacity-90"
                  style={{
                    touchAction: 'manipulation'
                  }}
                >
                  {/* Corner decorations */}
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-white/10 group-hover:border-white transition-colors" />
                  <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-white/10 group-hover:border-white transition-colors" />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-space text-2xl font-black mb-1 tracking-tight text-white">{t('dashboard.grammarExercises.title', { defaultValue: 'Grammar Exercises' })}</h3>
                        <p className="text-sm text-white/60 font-medium leading-relaxed">
                          {grammarPreviewExercise?.pillarTitle
                            ? grammarPreviewExercise.pillarTitle
                            : grammarTopics.length > 0
                              ? t('dashboard.grammarExercises.subtitle', { count: grammarTopics.length, defaultValue: `${grammarTopics.length} topics to master` })
                              : t('dashboard.grammarExercises.emptySubtitle', { defaultValue: 'Practice now' })
                          }
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-500/20 text-blue-400 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 group-hover:bg-blue-500/30 transition-all duration-300">
                        <Brain className="w-6 h-6" />
                      </div>
                    </div>

                    {isLoadingData ? (
                      <div className="space-y-2 animate-pulse">
                        <div className="h-10 bg-white/10 rounded-lg"></div>
                        <div className="h-10 bg-white/10 rounded-lg"></div>
                      </div>
                    ) : grammarPreviewExercise ? (
                      <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="text-[10px] font-bold tracking-widest uppercase text-white/40">Sample</span>
                          <span className="text-[10px] text-white/40">·</span>
                          <span className="text-[10px] font-bold tracking-widest uppercase text-blue-400">
                            {exerciseTypeLabel(grammarPreviewExercise.exerciseType)}
                          </span>
                        </div>

                        {grammarPreviewExercise.exerciseType === 'sentenceBuilding' && (() => {
                          const words = grammarPreviewExercise.stimulus || [];
                          const mid = Math.ceil(words.length / 2);
                          const shuffled = [...words.slice(mid), ...words.slice(0, mid)];
                          return (
                            <div className="rounded-xl border border-white/10 bg-zinc-900 p-3 pointer-events-none select-none">
                              <p className="text-[10px] text-white/50 mb-2">Arrange the words:</p>
                              <div className="flex flex-wrap gap-1.5">
                                {shuffled.map((word, i) => (
                                  <span key={i} className="px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-xs font-medium text-white">
                                    {word}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {grammarPreviewExercise.exerciseType === 'multipleChoice' && (
                          <div className="rounded-xl border border-white/10 bg-zinc-900 p-3 pointer-events-none select-none">
                            <p className="text-xs text-white/80 leading-snug mb-2 line-clamp-2">
                              {(grammarPreviewExercise.stimulus || '').replace(/(__+)|(\[---\])/g, '______')}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {(grammarPreviewExercise.options || []).map((opt, i) => (
                                <span key={i} className="px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-xs text-white/80">
                                  {opt}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {grammarPreviewExercise.exerciseType === 'matchingPairs' && (
                          <div className="rounded-xl border border-white/10 bg-zinc-900 p-3 pointer-events-none select-none">
                            <p className="text-[10px] text-white/50 mb-2">Match the pairs:</p>
                            <div className="grid grid-cols-2 gap-1.5">
                              {(grammarPreviewExercise.correctAnswer || []).slice(0, 3).map((pair, i) => (
                                <React.Fragment key={i}>
                                  <span className="px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-xs text-white/80 text-center truncate">{pair.left}</span>
                                  <span className="px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-xs text-white/80 text-center truncate">{pair.right}</span>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        )}

                        {grammarPreviewExercise.exerciseType === 'errorCorrection' && (
                          <div className="rounded-xl border border-white/10 bg-zinc-900 p-3 pointer-events-none select-none">
                            <p className="text-[10px] text-white/50 mb-2">Find &amp; fix the error:</p>
                            <p className="text-xs text-white/80 leading-snug line-clamp-2">
                              {(grammarPreviewExercise.stimulus || '').replace(
                                grammarPreviewExercise.incorrectPart || '',
                                `[${grammarPreviewExercise.incorrectPart || ''}]`
                              )}
                            </p>
                          </div>
                        )}

                        {(grammarPreviewExercise.exerciseType === 'fillInTheBlanks' || grammarPreviewExercise.exerciseType === 'sentenceTransformation') && (
                          <div className="rounded-xl border border-white/10 bg-zinc-900 p-3 pointer-events-none select-none">
                            <p className="text-xs text-white/80 leading-snug line-clamp-2">
                              {typeof grammarPreviewExercise.stimulus === 'string'
                                ? grammarPreviewExercise.stimulus.replace(/(__+)|(\[---\])/g, '______')
                                : grammarPreviewExercise.question}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-center gap-1.5 mt-3">
                          <ArrowRight className="w-4 h-4 text-blue-400 animate-pulse" />
                          <span className="text-xs text-white/60 font-medium">
                            {t('dashboard.grammarExercises.action', { defaultValue: 'Start Exercises →' })}
                          </span>
                        </div>
                      </div>
                    ) : grammarTopics.length > 0 ? (
                      <div className="space-y-2">
                        {grammarTopics.map((topic, i) => (
                          <div key={i} className="bg-white/10 rounded-lg px-4 py-2.5 flex items-center justify-between border border-white/10 group-hover:bg-white/15 transition-colors">
                            <span className="text-sm font-medium text-white leading-relaxed">{topic.name}</span>
                            <span className="text-xs text-blue-400 font-bold">
                              {topic.subtopics?.length || 0} {t('dashboard.grammarExercises.topics', { defaultValue: 'topics' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border-2 border-blue-500/30">
                        <p className="text-center text-white font-space text-base font-bold mb-2 leading-relaxed">
                          {t('dashboard.grammarExercises.cta', {
                            defaultValue: 'Master essential grammar topics'
                          })}
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-3">
                          <ArrowRight className="w-5 h-5 animate-pulse" />
                          <span className="text-sm text-white/80 font-medium leading-relaxed">
                            {t('dashboard.grammarExercises.action', { defaultValue: 'Start Exercises →' })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Shine effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>

                  {/* Particle dot */}
                  <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-blue-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 transform scale-100 sm:scale-0 sm:group-hover:scale-100" />
                </div>
              );
            })()}
          </div>

          {/* Two Column Row: Weekly Progress & Achievements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* This Week - White */}
            <div
              onClick={() => openPanel('weekly')}
              className="group relative bg-white text-slate-900 rounded-2xl p-4 sm:p-6 cursor-pointer border border-gray-200 shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 active:opacity-90"
              style={{
                touchAction: 'manipulation'
              }}
            >
              {/* Corner decorations */}
              <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-gray-300 group-hover:border-slate-900 transition-colors" />
              <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-gray-300 group-hover:border-slate-900 transition-colors" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-space text-2xl font-black text-slate-900 mb-1 tracking-tight">{t('dashboard.weeklyProgress.title')}</h3>
                    <p className="text-sm text-gray-600 font-medium leading-relaxed">
                      {t('dashboard.weeklyProgress.daysActive', { count: activeDays })}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-gray-200 text-slate-900 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:bg-gray-300 transition-all duration-300">
                    <Calendar className="w-7 h-7" />
                  </div>
                </div>

                <div className="flex justify-between gap-1 sm:gap-2 items-end h-28 min-h-[7rem]">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => {
                    const dayData = finalWeeklyData[i] || { lessons: 0, minutes: 0 };
                    const maxMinutes = 120;
                    const minutes = dayData.minutes || 0;
                    const active = minutes > 0 || dayData.lessons > 0;
                    const percentage = minutes > 0 ? Math.min((minutes / maxMinutes) * 100, 100) : 0;
                    const containerHeight = 112;
                    const minHeight = 6;
                    const activeMinHeight = 12;
                    const maxHeight = containerHeight - 24;
                    const heightPx = active 
                      ? Math.max((percentage / 100) * maxHeight, activeMinHeight)
                      : minHeight;

                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1 sm:gap-2 min-w-0">
                        <div
                          className={`w-full rounded-lg transition-all duration-300 flex-shrink-0 ${
                            active ? 'bg-slate-900' : 'bg-gray-200'
                          }`}
                          style={{ height: `${heightPx}px`, minHeight: `${heightPx}px` }}
                        />
                        <span className={`text-xs font-bold flex-shrink-0 ${active ? 'text-slate-900' : 'text-gray-400'}`}>
                          {day}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-100/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
            </div>

            {/* Achievements - Black */}
            <div
              onClick={() => openPanel('achievements')}
              className="group relative bg-black text-white rounded-2xl p-4 sm:p-6 cursor-pointer border border-white/10 transition-all duration-500 hover:-translate-y-1 active:opacity-90"
              style={{
                touchAction: 'manipulation'
              }}
            >
              {/* Corner decorations */}
              <div className="absolute top-1 left-1 sm:top-2 sm:left-2 w-4 h-4 border-t-2 border-l-2 border-white/10 group-hover:border-white transition-colors" />
              <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 w-4 h-4 border-b-2 border-r-2 border-white/10 group-hover:border-white transition-colors" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-space text-2xl font-black text-white mb-1 tracking-tight">{t('dashboard.achievements.title')}</h3>
                    <p className="text-sm text-white/60 font-medium leading-relaxed">
                      {unlockedAchievements} {t('gamification.achievements.earned')}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-white/10 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-white/20 transition-all duration-300">
                    <Trophy className="w-7 h-7" />
                  </div>
                </div>

                <div className="flex gap-3">
                  {[1, 2, 3].map(i => (
                    <div
                      key={i}
                      className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10 group-hover:scale-110 transition-transform duration-300"
                      style={{ transitionDelay: `${i * 50}ms` }}
                    >
                      <Trophy className="w-8 h-8 text-white" />
                    </div>
                  ))}
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center border border-dashed border-white/20 transition-colors">
                    <span className="font-space text-xl font-black text-white/60">+{Math.max(0, unlockedAchievements - 3)}</span>
                  </div>
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
            </div>
          </div>

          </div>{/* end space-y-4 sm:space-y-6, general training */}
          </div>{/* end overflow-hidden, general training */}
          </div>{/* end collapsible grid, general training section */}
          </div>{/* end general training section group */}

          </div>{/* end purpose-ordered flex container */}

        </div>
      </div>

      {/* Backdrop Overlay */}
      {(panelOpen || examAnalyticsOpen) && (
        <div
          className="fixed inset-0 bg-black opacity-30 backdrop-blur-sm transition-opacity duration-500 z-40"
          onClick={() => {
            if (panelOpen) closePanel();
            if (examAnalyticsOpen) setExamAnalyticsOpen(false);
          }}
        />
      )}

      {/* Slide-Out Panel */}
      <SlideOutPanel
        isOpen={panelOpen}
        onClose={closePanel}
        title={getPanelTitle(activePanel, t)}
      >
        {activePanel === 'activity' && (
          <ActivityDetailPanel
            recentActivity={recentActivity}
            userProgress={userProgress}
            onNavigate={handleNavigation}
          />
        )}
        {activePanel === 'weekly' && (
          <WeeklyDetailPanel
            weeklyData={finalWeeklyData}
            onNavigate={handleNavigation}
          />
        )}
        {activePanel === 'vocabulary' && (
          <VocabularyDetailPanel
            vocabularyData={finalVocabData}
            vocabularyStats={vocabularyStats}
            onNavigate={handleNavigation}
            userProgress={userProgress}
            examLevel={currentExamData?.examLevel}
          />
        )}
        {activePanel === 'achievements' && (
          <AchievementsDetailPanel
            achievementStats={achievementStats}
            userProgress={userProgress}
            onNavigate={handleNavigation}
          />
        )}
        {activePanel === 'mistakes' && (
          <MistakesDetailPanel
            mistakesData={finalMistakesData}
            onNavigate={handleNavigation}
          />
        )}
      </SlideOutPanel>

      {/* Exam Analytics Panel - Wider for better data display */}
      <SlideOutPanel
        isOpen={examAnalyticsOpen}
        onClose={() => setExamAnalyticsOpen(false)}
        wide={true}
        title={examAnalyticsData?.examData 
          ? `${examAnalyticsData.examData.examProvider} ${examAnalyticsData.examData.level} ${t('dashboard.examAnalytics.analytics', { defaultValue: 'Analytics' })}`
          : t('dashboard.examAnalytics.title', { defaultValue: 'Exam Analytics' })
        }
      >
        {examAnalyticsData?.examData ? (
          <div className="p-4">
            <ExamAnalytics 
              currentExam={examAnalyticsData.currentExam} 
              examData={examAnalyticsData.examData} 
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 text-gray-400 mb-4" />
            <p className="text-gray-700 font-semibold text-lg mb-2">
              {t('dashboard.examAnalytics.noData', { defaultValue: 'No exam data available' })}
            </p>
            <p className="text-gray-500 text-sm text-center max-w-md leading-relaxed">
              {t('dashboard.examAnalytics.completeExams', { defaultValue: 'Complete some exams to see analytics.' })}
            </p>
          </div>
        )}
      </SlideOutPanel>
    </div>
  );
};

// Helper: Detect Recent Activity - REAL DATA
const detectRecentActivity = (userProgress, t) => {
  // Check for zero-state: no lessons completed today AND empty completedLessons array
  const todayLessons = userProgress?.todayLessons || 0;
  const completedLessons = userProgress?.completedLessons || [];
  const isZeroState = todayLessons === 0 && completedLessons.length === 0;

  // Zero-state: Show Study Plan Wizard CTA
  if (isZeroState) {
    return {
      type: 'onboarding',
      isZeroStateCTA: true,
      title: t('dashboard.zeroState.heroCard.title', { 
        defaultValue: 'Step 1: Build Your Personalized Learning Plan' 
      }),
      subtitle: t('dashboard.zeroState.heroCard.subtitle', { 
        defaultValue: "Let Logami's AI map out your fastest route to passing the exam." 
      }),
      progress: null,
      path: '/study-plan/wizard'
    };
  }

  if (userProgress?.grammarContinuation?.pillarId) {
    const cont = userProgress.grammarContinuation;
    return {
      type: 'grammar',
      title: `${t('dashboard.hero.grammar.title')}: ${cont.topicName || 'Continue'}`,
      subtitle: t('dashboard.actionZone.continueGrammar.description', {
        level: userProgress.grammarStats?.activeLevel || 'B1',
        exerciseInfo: ''
      }),
      progress: cont.exerciseIndex ? {
        current: cont.exerciseIndex,
        total: 12,
        percentage: Math.round((cont.exerciseIndex / 12) * 100)
      } : null,
      path: `/lessons`
    };
  }

  if (userProgress?.lastVocabCategory) {
    return {
      type: 'vocabulary',
      title: `${t('dashboard.hero.vocabulary.title')}: ${userProgress.lastVocabCategory}`,
      subtitle: t('dashboard.actionZone.dueReview.description'),
      progress: null,
      path: '/practice/vocabulary'
    };
  }

  // Check if daily goal is actually completed
  // Check preferences.dailyGoal first (where it's actually stored), then top-level dailyGoal, then default to 5
  const dailyGoal = userProgress?.preferences?.dailyGoal || userProgress?.dailyGoal || 5;
  const isDailyGoalMet = todayLessons >= dailyGoal;

  // Only show "All Caught Up" if daily goal is actually met
  if (isDailyGoalMet) {
    return {
      type: 'lesson',
      title: t('dashboard.actionZone.allCaughtUp.title'),
      subtitle: t('dashboard.actionZone.allCaughtUp.description') + ' ' + t('dashboard.actionZone.allCaughtUp.note', { defaultValue: '(This refers to your daily goal, not your overall study plan.)' }),
      progress: null,
      path: '/lessons'
    };
  }

  // If daily goal is not met, show encouragement to start practicing
  const remaining = dailyGoal - todayLessons;
  return {
    type: 'lesson',
    title: remaining === 1 
      ? t('dashboard.actionZone.dailyGoal.title', { remaining })
      : t('dashboard.actionZone.dailyGoal.title_plural', { remaining }),
    subtitle: t('dashboard.actionZone.dailyGoal.description'),
    progress: {
      current: todayLessons,
      total: dailyGoal,
      percentage: Math.round((todayLessons / dailyGoal) * 100)
    },
    path: '/lessons' // Navigate to study plan page
  };
};

// Helper: Get Panel Title
const getPanelTitle = (panel, t) => {
  const titles = {
    activity: '🔥 ' + t('dashboard.actionZone.title'),
    weekly: '📊 ' + t('dashboard.weeklyProgress.title'),
    vocabulary: '📚 ' + t('dashboard.vocabularyCategories.title'),
    achievements: '🏆 ' + t('dashboard.achievements.title'),
    mistakes: '⚠️ ' + t('dashboard.mistakes.title')
  };
  return titles[panel] || '';
};

// Helper: Greeting
const getGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t('dashboard.greeting.goodMorning');
  if (hour < 17) return t('dashboard.greeting.goodAfternoon');
  return t('dashboard.greeting.goodEvening');
};

// Loading Skeleton - Redesigned
const LoadingSkeleton = ({ t }) => (
  <div className="relative min-h-screen bg-white">
    <div className="relative z-10 p-8 max-w-7xl mx-auto space-y-6 animate-pulse">
      <div className="h-12 bg-black/10 rounded-lg w-1/3 mb-8"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-40 bg-white shadow-sm border border-slate-200/60 rounded-2xl"></div>
        ))}
      </div>
      <div className="h-56 bg-white shadow-sm border border-slate-200/60 rounded-3xl"></div>
      <div className="grid grid-cols-2 gap-6">
        <div className="h-48 bg-white shadow-sm border border-slate-200/60 rounded-2xl"></div>
        <div className="h-48 bg-white shadow-sm border border-slate-200/60 rounded-2xl"></div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="h-40 bg-white shadow-sm border border-slate-200/60 rounded-2xl"></div>
        <div className="h-40 bg-white shadow-sm border border-slate-200/60 rounded-2xl"></div>
      </div>
      <p className="text-center text-black/50 font-medium">{t('dashboard.loading')}</p>
    </div>
  </div>
);

export default CompactDashboard;